import { type ReactNode, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { resolve, type Shortlink } from '@/entities/shortlink';
import { ChangePage } from '@/pages/change/change';
import { CouponPage } from '@/pages/coupon/coupon';
import { useAsync } from '@/shared/lib/use-async';
import { Notice } from '@/shared/ui/notice/notice';

function titleFor(result: Shortlink | null | undefined): string {
  if (result?.status === 'ok') return '쿠폰 교환';
  if (result?.status === 'changed') return '교환쿠폰';
  return '안내';
}

export function Entry() {
  const { uri } = useParams();
  const { data, loading, error, reload } = useAsync<Shortlink | null>(
    () => (uri ? resolve(uri) : Promise.resolve(null)),
    [uri],
  );
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (loading) return;
    document.title = `${titleFor(data)} - 체인지콘`;
    rootRef.current?.focus();
  }, [loading, data]);

  let content: ReactNode;
  if (loading) {
    content = (
      <div className="route-fallback" role="status">
        쿠폰을 불러오는 중이에요…
      </div>
    );
  } else if (error) {
    content = (
      <div className="route-fallback">
        <p>페이지를 불러오지 못했어요 😥</p>
        <button type="button" className="route-fallback__retry" onClick={reload}>
          다시 시도
        </button>
      </div>
    );
  } else if (!data) {
    content = <Notice />;
  } else if (data.status === 'ok') {
    content = <ChangePage preloaded={data} token={data.token} />;
  } else if (data.status === 'changed') {
    content = <CouponPage token={data.seqnumber} />;
  } else {
    content = <Notice message={data.message} />;
  }

  return (
    <div ref={rootRef} className="route-content" tabIndex={-1}>
      {content}
    </div>
  );
}
